import { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Autocomplete,
  Typography,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
// removed unused icons
import { getDrivers } from "services/driverService";
import { TransactionTypeOptions, TransactionTypeEnum } from "constants/transactionTypeConstants";

// Keep labels aligned with backend TransactionTypeEnum
// const transactionTypeEnum = {
//   1: "Top Up",
//   2: "Fanoos Fee",
//   3: "Incentive",
//   4: "Adjustment",
//   5: "Withdrawal",
//   6: "Transaction Fee",
//   7: "Cancellation Fees",
//   8: "Refund",
// };

const paymentMethodEnum = {
  1: "Cash",
  2: "Bank Transfer",
  3: "E-Fawateer",
  4: "Card",
  5: "Wallet",
};

const WalletTransactionForm = ({
  initialData,
  onSubmit,
  isLoading,
  drivers = [],
  driversLoading,
}) => {
  const [formData, setFormData] = useState({
    driverIds: [],
    amount: "",
    transactionType: "",
    paymentMethod: "",
    files: [],
  });

  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [driverInputValue, setDriverInputValue] = useState("");
  const driverSearchRef = useRef(null);
  const [driversOptions, setDriversOptions] = useState(drivers || []);
  const [dragActive, setDragActive] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const focusField = (fieldId) => {
    const el = document.getElementById(fieldId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      try { el.focus(); } catch (e) {}
    }
  };

  const pushNotification = ({ message, severity = "error", field = "" }) => {
    const id = Date.now() + Math.random();
    setNotifications((s) => [...s, { id, message, severity, field }]);
  };

  const removeNotification = (id) => setNotifications((s) => s.filter((n) => n.id !== id));

  // ✅ Initialize from parent
  useEffect(() => {
    if (!initialData) return;

    setFormData((prev) => ({
      ...prev,
      driverIds: initialData.driverIds || [],
      amount: initialData.amount || "",
      transactionType: initialData.transactionType || "",
      paymentMethod: initialData.paymentMethod || "",
    }));

    setSelectedDrivers(
      drivers.filter((d) =>
        (initialData.driverIds || []).includes(d.id)
      )
    );
    setDriversOptions(drivers || []);
  }, [initialData, drivers]);

  // ---------------- HANDLERS ----------------

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        files: Array.from(files),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDriversChange = (_, value) => {
    setSelectedDrivers(value);
    setFormData((prev) => ({
      ...prev,
      driverIds: value.map((d) => d.id),
    }));
    // do not clear the driver's input text on selection; keep it stable
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { driverIds, amount, transactionType, paymentMethod } = formData;

    const errors = [];
    if (!driverIds.length) errors.push({ field: "drivers", message: "Please select at least one driver." });
    if (!amount || Number(amount) <= 0) errors.push({ field: "amount", message: "Please enter a valid amount." });
    if (!transactionType) errors.push({ field: "transactionType", message: "Please select a transaction type." });
    if (!paymentMethod) errors.push({ field: "paymentMethod", message: "Please select a payment method." });

    if (errors.length) {
      errors.forEach((err) => pushNotification({ message: err.message, severity: "error", field: err.field }));
      return;
    }

    try {
      await Promise.resolve(onSubmit(formData));
    } catch (err) {
      console.error(err);
      pushNotification({ message: "Failed to save transaction. Please try again.", severity: "error" });
    }
  };

  // ---------------- FILE HANDLING ----------------

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files?.length) {
      setFormData((prev) => ({
        ...prev,
        files: Array.from(e.dataTransfer.files),
      }));
    }
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const getFileIcon = () => <InsertDriveFileIcon sx={{ fontSize: 16 }} />;

  // ---------------- UI ----------------

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        {/* Drivers */}
        <Autocomplete
          multiple
          options={driversOptions}
          loading={driversLoading}
          value={selectedDrivers}
          onChange={handleDriversChange}
          inputValue={driverInputValue}
          onInputChange={(e, v) => {
            setDriverInputValue(v);
            if (driverSearchRef.current) clearTimeout(driverSearchRef.current);
            driverSearchRef.current = setTimeout(async () => {
              if (!v || v.trim() === "") {
                setDriversOptions(drivers || []);
                return;
              }
              try {
                const [r1, r2] = await Promise.all([
                  getDrivers(100, 1, v, "", "", [], null, null, null, null, null, 1),
                  getDrivers(100, 1, v, "", "", [], null, null, null, null, null, 2),
                ]);
                const a = r1?.page || [];
                const b = r2?.page || [];
                const combined = [...a];
                const ids = new Set(a.map((d) => String(d.id)));
                for (const d of b) {
                  if (!ids.has(String(d.id))) {
                    combined.push(d);
                    ids.add(String(d.id));
                  }
                }
                setDriversOptions(combined || []);
              } catch (err) {
                console.error('driver search failed', err);
              }
            }, 300);
          }}
          getOptionLabel={(o) =>
            `${o.fullName || "Unknown"}${o.phone ? ` (${o.phone})` : ""}`
          }
          isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
          renderInput={(params) => (
            <TextField {...params} id="drivers" label="Drivers" />
          )}
        />

        {/* Amount */}
        <TextField
          id="amount"
          label="Amount (JOD)"
          name="amount"
          type="number"
          value={formData.amount}
          onChange={handleChange}
          required
        />

        {/* Transaction Type */}
        <FormControl fullWidth required>
          <InputLabel>Transaction Type</InputLabel>
          <Select
            id="transactionType"
            name="transactionType"
            value={formData.transactionType}
            onChange={handleChange}
            label="Transaction Type"
          >
            {TransactionTypeOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Payment Method */}
        <FormControl fullWidth required>
          <InputLabel>Payment Method</InputLabel>
          <Select
            id="paymentMethod"
            name="paymentMethod"
            value={formData.paymentMethod}
            onChange={handleChange}
            label="Payment Method"
          >
            {Object.entries(paymentMethodEnum).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Files */}
        <Paper
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            p: 2,
            textAlign: "center",
            border: "2px dashed #ccc",
            bgcolor: dragActive ? "#e3f2fd" : "#fafafa",
          }}
        >
          <CloudUploadIcon />
          <Typography variant="body2">Drag & drop or select files</Typography>

          <Button component="label" startIcon={<AttachFileIcon />}>
            Select Files
            <input hidden multiple type="file" onChange={handleChange} name="files" />
          </Button>

          {formData.files.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                {formData.files.map((f, i) => (
                  <Chip
                    key={i}
                    label={f.name}
                    icon={getFileIcon()}
                    onDelete={() => handleRemoveFile(i)}
                  />
                ))}
              </Stack>
            </>
          )}
        </Paper>

        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          sx={{ backgroundColor: "#2A3467" }}
        >
          {isLoading ? "Saving..." : "Save Transaction"}
        </Button>
      </Stack>

      <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1400, display: "flex", flexDirection: "column", gap: 1 }}>
        {notifications.map((n, i) => (
          <Alert
            key={n.id}
            severity={n.severity}
            variant="filled"
            sx={{ minWidth: 320 }}
            action={
              n.field ? (
                <Button color="inherit" size="small" onClick={() => { focusField(n.field === "drivers" ? "drivers" : n.field); removeNotification(n.id); }}>
                  Go
                </Button>
              ) : null
            }
            onClose={() => removeNotification(n.id)}
          >
            {n.message}
          </Alert>
        ))}
      </Box>
    </Box>
  );
};

export default WalletTransactionForm;
